// /app/billing — wired to nexus-billing-service v0.1.0.
//
// Reflects the operator's subscription + topup credit model:
//   - Current plan card (Starter/Growth/Agency/Enterprise) showing
//     monthly allowance + renewal date
//   - Credit pool card splitting subscription_balance vs topup_balance
//     so it's visible that topup credits PERSIST past the monthly reset
//   - Plan catalog grid with upgrade CTA
//   - Credit-pack picker with checkout redirect
//   - Invoice history table (subscription + topup interleaved)

import { useState, useEffect, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Icons } from "@/components/icons";
import { PageHead, Tabs } from "@/components/primitives";

const TAB_IDS = ["overview", "plans", "credits", "usage", "invoices"] as const;
type TabSlug = typeof TAB_IDS[number];

export const Route = createFileRoute("/app/billing")({
  component: BillingPage,
  // Accept ?tab=… so deep-links from QuotaExceededModal, BrandHealthGuide,
  // etc. can land on the right pane without an extra hop.
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: TabSlug; payment?: "success" | "failed" } => {
    const out: { tab?: TabSlug; payment?: "success" | "failed" } = {};
    const t = search.tab;
    if (typeof t === "string" && (TAB_IDS as readonly string[]).includes(t)) {
      out.tab = t as TabSlug;
    }
    // iyzico checkout redirects back here with ?payment=success|failed.
    if (search.payment === "success" || search.payment === "failed") {
      out.payment = search.payment;
    }
    return out;
  },
});

// Plan + pack shapes match billing-service v0.2.1+ (DB-backed catalog,
// region-aware pricing). The legacy fields (price_try, monthly_credits)
// are kept on the type but the dashboard prefers the region-specific
// amount / currency / vat_rate when present.
type PlanTier = {
  id: string;
  slug?: string;
  name: string;
  tagline: string;
  interval?: string;
  // Legacy stub fields (still set by the static-fallback path):
  price_try?: number;
  monthly_credits?: number;
  // DB-backed fields:
  region?: string;        // "international" | "turkey"
  currency?: string;      // "USD" | "TRY" | "EUR" | ...
  amount?: number;        // pre-VAT
  vat_rate?: number;      // 0.0 | 0.2
  amount_with_vat?: number;
  monthly_generations?: number;
  monthly_publications?: number;
  is_unlimited: boolean;
  is_trial?: boolean;
  trial_days?: number;
  features: string[];
  max_brands: number;
};

type MarketEnvelope = {
  region?: string;
  currency?: string;
  country_code?: string;
  lang?: string;
  interval?: string;
};

type PlanResponse = {
  plan?: PlanTier;
  started_at?: string;
  renewal_at?: string;
  cancel_at?: string;
  market?: MarketEnvelope;
} & Partial<PlanTier>; // legacy fallback shape

type CreditsResponse = {
  subscription_balance: number;
  topup_balance: number;
  total_balance: number;
  monthly_allowance: number;
  is_unlimited: boolean;
  balance_label: string;
  period_start: string;
  period_end: string;
  consumed_this_cycle: number;
  last_topup_at?: string;
  auto_topup_enabled: boolean;
  auto_topup_threshold: number;
  auto_topup_pack_id: string;
  // Phase 4 trial fields:
  is_trial?: boolean;
  trial_ends_at?: string | null;
  trial_days_remaining?: number;
  plan_slug?: string;
  status?: string;
};

type CreditPack = {
  id: string;
  slug?: string;
  name: string;
  credits: number;
  badge?: string;
  // Legacy:
  price_try?: number;
  price_per_credit?: string;
  // DB-backed:
  region?: string;
  currency?: string;
  amount?: number;
  vat_rate?: number;
  amount_with_vat?: number;
  price_per_unit?: number;
};

const CURRENCY_SYMBOL: Record<string, string> = {
  TRY: "₺",
  USD: "$",
  EUR: "€",
  GBP: "£",
  AED: "د.إ",
  SAR: "﷼",
  EGP: "ج.م",
};

function formatMoney(amount: number, currency?: string): string {
  const sym = CURRENCY_SYMBOL[currency || "USD"] || "";
  const fmt = new Intl.NumberFormat(currency === "TRY" ? "tr-TR" : "en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `${sym}${fmt.format(amount)}`;
}

type Invoice = {
  id: string;
  number: string;
  kind: "subscription" | "topup";
  period_start: string;
  period_end: string;
  amount_try: number;
  status: string;
  pdf_url: string;
};

type UsageResponse = {
  period: string;
  consumed: number;
  by_kind: { kind: string; credits: number }[];
  by_brand: { brand_id: string; credits: number }[];
  by_source: { source: string; credits: number }[];
};

type Tab = "overview" | "plans" | "credits" | "usage" | "invoices";

// Shape returned by /billing/subscribe and /billing/credits/buy.
type CheckoutInit = {
  checkout_url?: string;
  checkout_form_content?: string;
  token?: string;
};

// PaymentModal embeds iyzico's Checkout Form inline (in a modal on our own
// page) instead of redirecting to iyzico. It injects the returned
// checkoutFormContent <script> (which renders iyzico's secure card iframe
// into #iyzipay-checkout-form), and polls /billing/checkout/status for
// completion. On success the whole page lands on ?payment=success (the
// backend callback breaks out of the iframe; this poll is the belt-and-
// suspenders that also reloads if the break-out is blocked). Card fields
// live in iyzico's iframe, so our PCI posture is unchanged.
function PaymentModal({
  content,
  token,
  onClose,
}: {
  content: string;
  token: string;
  onClose: () => void;
}) {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const scriptHolder = useRef<HTMLDivElement | null>(null);

  // Inject + execute the iyzico form script once.
  useEffect(() => {
    const holder = scriptHolder.current;
    if (!holder) return;
    // createContextualFragment executes both inline and external <script>s,
    // unlike innerHTML. The script finds #iyzipay-checkout-form (rendered
    // below) and injects iyzico's card iframe there.
    try {
      const frag = document.createRange().createContextualFragment(content);
      holder.appendChild(frag);
    } catch {
      /* if injection fails, the poll/close still work */
    }
    return () => {
      holder.innerHTML = "";
    };
  }, [content]);

  // Poll for completion (the server-side callback flips the session).
  useEffect(() => {
    if (!token) return;
    let stop = false;
    const id = setInterval(async () => {
      try {
        const s = await api<{ status: string }>(
          `/billing/checkout/status?token=${encodeURIComponent(token)}`,
        );
        if (stop) return;
        if (s.status === "success") {
          clearInterval(id);
          window.location.href = "/app/billing?payment=success";
        } else if (s.status === "failed") {
          clearInterval(id);
          onClose();
          alert(tr ? "Ödeme tamamlanamadı." : "Payment could not be completed.");
        }
      } catch {
        /* transient — keep polling */
      }
    }, 2500);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [token, onClose, tr]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 1000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        overflowY: "auto",
        padding: "24px 12px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: "100%", maxWidth: 460, padding: 20, marginTop: "4vh" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <strong>{tr ? "Güvenli Ödeme" : "Secure Payment"}</strong>
          <button className="btn btn-secondary btn-sm" onClick={onClose} aria-label="close">✕</button>
        </div>
        <div ref={scriptHolder} />
        <div id="iyzipay-checkout-form" className="responsive" />
        <div style={{ marginTop: 10, fontSize: 11, color: "var(--mu)", textAlign: "center" }}>
          {tr ? "iyzico ile güvenli ödeme · kart bilgileriniz bize iletilmez" : "Secured by iyzico · your card details never reach us"}
        </div>
      </div>
    </div>
  );
}

function BillingPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  // Seed tab from ?tab= so deep-links (QuotaExceededModal → ?tab=credits,
  // BrandHealthGuide etc.) land on the right pane without an extra click.
  const search = Route.useSearch();
  const [tab, setTab] = useState<Tab>((search.tab as Tab) ?? "overview");
  // ?payment=success|failed — set by the iyzico callback redirect.
  const [paymentBanner, setPaymentBanner] = useState<"success" | "failed" | null>(
    search.payment ?? null,
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {paymentBanner ? (
        <div
          role="status"
          style={{
            margin: "16px 32px 0",
            padding: "12px 16px",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 13,
            background: paymentBanner === "success" ? "rgba(46,160,67,0.12)" : "rgba(224,45,28,0.12)",
            border: `1px solid ${paymentBanner === "success" ? "rgba(46,160,67,0.5)" : "rgba(224,45,28,0.5)"}`,
          }}
        >
          <span style={{ flex: 1 }}>
            {paymentBanner === "success"
              ? tr
                ? "Ödeme alındı — planın aktifleştirildi. Hoş geldin! 🎉"
                : "Payment received — your plan is now active. Welcome aboard! 🎉"
              : tr
                ? "Ödeme tamamlanamadı. Kartından ücret alınmadıysa tekrar deneyebilirsin."
                : "Payment didn't complete. If your card wasn't charged, you can try again."}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setPaymentBanner(null)}>✕</button>
        </div>
      ) : null}
      <PageHead
        eyebrow={tr ? "13 · Faturalama" : "13 · Billing & credits"}
        title={tr ? "Plan, krediler ve faturalar." : "Plans, credits, and invoices."}
        subtitle={tr
          ? "Abonelik aylık kredi içerir; ek paketler her zaman satın alınabilir ve aylık sıfırlamadan etkilenmez."
          : "Subscription includes monthly credits; extra packs can be bought anytime and persist past the monthly reset."}
      />
      <div style={{ padding: "0 32px", borderBottom: "1px solid var(--b1)" }}>
        <Tabs<Tab>
          value={tab}
          onChange={setTab}
          tabs={[
            { id: "overview", label: tr ? "Genel bakış" : "Overview" },
            { id: "plans", label: tr ? "Planlar" : "Plans" },
            { id: "credits", label: tr ? "Kredi paketleri" : "Credit packs" },
            { id: "usage", label: tr ? "Kullanım" : "Usage" },
            { id: "invoices", label: tr ? "Faturalar" : "Invoices" },
          ]}
        />
      </div>
      <div className="scroll-y" style={{ flex: 1, padding: "24px 32px 48px" }}>
        {tab === "overview" ? <Overview onTab={setTab} /> : null}
        {tab === "plans" ? <PlansGrid /> : null}
        {tab === "credits" ? <CreditPacksTab /> : null}
        {tab === "usage" ? <UsageTab /> : null}
        {tab === "invoices" ? <InvoicesTable /> : null}
      </div>
    </div>
  );
}

function Overview({ onTab }: { onTab: (t: Tab) => void }) {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const { data: planRes } = useQuery({ queryKey: ["billing", "plan", i18n.resolvedLanguage], queryFn: () => api<PlanResponse>(`/billing/plan?lang=${i18n.resolvedLanguage}`) });
  const { data: credits } = useQuery({ queryKey: ["billing", "credits"], queryFn: () => api<CreditsResponse>("/billing/credits") });
  if (!planRes || !credits) return <SkeletonRow />;

  // Unwrap v0.2.x shape ({plan, started_at, ...}) vs the legacy flat
  // shape so the rest of the component keeps the existing prop names.
  const plan = (planRes.plan ?? (planRes as PlanTier));
  const subPct = credits.monthly_allowance > 0
    ? Math.max(0, Math.min(100, Math.round((credits.subscription_balance / credits.monthly_allowance) * 100)))
    : 100;

  // Trial / expiry banner — Phase 4 of the billing system. Read from
  // /v1/billing/credits which carries is_trial + trial_days_remaining
  // + status (trialing|active|past_due|expired).
  const trialDays = credits.trial_days_remaining ?? 0;
  const trialBanner =
    credits.is_trial && trialDays > 0 ? (
      <div
        className="card"
        style={{
          padding: "12px 16px",
          marginBottom: 16,
          background: trialDays <= 3 ? "rgba(245,158,11,0.08)" : "rgba(34,197,94,0.06)",
          borderColor: trialDays <= 3 ? "rgba(245,158,11,0.4)" : "rgba(34,197,94,0.3)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <Icons.Clock size={16} style={{ color: trialDays <= 3 ? "#F59E0B" : "var(--success)" }} />
        <div style={{ flex: 1, fontSize: 13 }}>
          <b>
            {tr
              ? `Ücretsiz deneme · ${trialDays} gün kaldı`
              : `Free trial · ${trialDays} day${trialDays === 1 ? "" : "s"} left`}
          </b>
          <div style={{ fontSize: 11, color: "var(--tx2)", marginTop: 2 }}>
            {tr
              ? "Bitmeden önce plan seç — yayınların kesintisiz devam etsin."
              : "Pick a plan before it ends so publishing keeps flowing."}
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => onTab("plans")}>
          {tr ? "Plan seç" : "Pick a plan"}
        </button>
      </div>
    ) : credits.status === "expired" ? (
      <div
        className="card"
        style={{
          padding: "12px 16px",
          marginBottom: 16,
          background: "rgba(239,68,68,0.08)",
          borderColor: "rgba(239,68,68,0.4)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <Icons.Alert size={16} style={{ color: "var(--error)" }} />
        <div style={{ flex: 1, fontSize: 13 }}>
          <b>{tr ? "Deneme süresi bitti" : "Trial expired"}</b>
          <div style={{ fontSize: 11, color: "var(--tx2)", marginTop: 2 }}>
            {tr
              ? "Yayınlamak için bir plan seç. Önceki içerikler taslak olarak korunur."
              : "Pick a plan to keep publishing. Existing content stays as drafts."}
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => onTab("plans")}>
          {tr ? "Plan seç" : "Pick a plan"}
        </button>
      </div>
    ) : null;

  return (
    <>
      {trialBanner}
      <div className="bill-2col" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="card" style={{ padding: 28, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -100, right: -100, width: 360, height: 360, background: "radial-gradient(circle, var(--accent-soft), transparent 70%)", filter: "blur(40px)", pointerEvents: "none" }} />
          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 18, marginBottom: 20 }}>
              <div style={{ width: 48, height: 48, borderRadius: 10, background: "var(--accent)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icons.Zap size={22} />
              </div>
              <div style={{ flex: 1 }}>
                <div className="eyebrow" style={{ marginBottom: 6 }}>— {tr ? "Aktif plan" : "Active plan"}</div>
                <h2 style={{ fontSize: 24, marginBottom: 4 }}>{plan.name}</h2>
                <div style={{ color: "var(--tx2)", fontSize: 13 }}>{plan.tagline}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="t-num" style={{ fontSize: 28 }}>
                  {(plan.amount ?? plan.price_try ?? 0) === 0
                    ? "—"
                    : formatMoney(plan.amount ?? plan.price_try ?? 0, plan.currency ?? "USD")}
                </div>
                <div className="mono" style={{ fontSize: 10, color: "var(--mu)", textTransform: "uppercase", letterSpacing: "0.1em" }}>/ {tr ? "ay" : "month"}</div>
                {(plan.vat_rate ?? 0) > 0 ? (
                  <div className="mono" style={{ fontSize: 9, color: "var(--mu)", marginTop: 4 }}>
                    + %{Math.round((plan.vat_rate ?? 0) * 100)} KDV
                  </div>
                ) : null}
              </div>
            </div>
            <div className="bill-3col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
              <Metric
                label={tr ? "Aylık gönderi" : "Monthly posts"}
                value={plan.is_unlimited ? "∞" : (plan.monthly_publications ?? plan.monthly_credits ?? 0).toLocaleString()}
              />
              <Metric label={tr ? "Marka limiti" : "Brand limit"} value={plan.max_brands === 0 ? (tr ? "Sınırsız" : "Unlimited") : String(plan.max_brands)} />
              <Metric label={tr ? "Yenileme" : "Renewal"} value={planRes.renewal_at ? new Date(planRes.renewal_at).toLocaleDateString(i18n.resolvedLanguage) : "—"} />
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: 28 }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Kredi havuzu" : "Credit pool"}</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
            <span className="t-num" style={{ fontSize: 44 }}>{credits.balance_label}</span>
            <span style={{ fontSize: 13, color: "var(--mu)" }}>{tr ? "toplam bakiye" : "total balance"}</span>
          </div>
          {credits.is_unlimited ? (
            <div style={{ fontSize: 13, color: "var(--tx2)" }}>{tr ? "Sınırsız plan — krediler dolu kalır." : "Unlimited plan — pool always full."}</div>
          ) : (
            <>
              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--mu)", marginBottom: 6 }}>
                  <span className="mono" style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}>{tr ? "Abonelik" : "Subscription"} · {credits.subscription_balance}/{credits.monthly_allowance}</span>
                  <span className="mono" style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}>{tr ? "Harcanan" : "Consumed"} · {credits.consumed_this_cycle}</span>
                </div>
                <div style={{ height: 8, background: "var(--elev)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${subPct}%`, height: "100%", background: subPct > 30 ? "var(--success)" : subPct > 10 ? "var(--warning)" : "var(--error)", transition: "width 250ms" }} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <PoolCard label={tr ? "Abonelik" : "Subscription"} sub={tr ? "her ay yenilenir" : "resets monthly"} value={credits.subscription_balance} tone="var(--info)" />
                <PoolCard label={tr ? "Ek paket" : "Topup"} sub={tr ? "kalıcı" : "persistent"} value={credits.topup_balance} tone="var(--purple)" />
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="card" style={{ padding: 20 }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Hızlı eylemler" : "Quick actions"}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              className="btn btn-primary"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={() => onTab("credits")}
            >
              <Icons.Plus size={14} /> {tr ? "Kredi satın al" : "Buy credits"}
            </button>
            <button
              className="btn btn-secondary"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={() => onTab("credits")}
              title={tr ? "Otomatik topup ayarları kredi paketleri sekmesinde" : "Auto-topup settings live under Credit packs"}
            >
              <Icons.Sliders size={14} /> {tr ? "Otomatik topup" : "Auto-topup"}
            </button>
            <button
              className="btn btn-secondary"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={() => onTab("plans")}
            >
              <Icons.ArrowUp size={14} /> {tr ? "Planı yükselt" : "Upgrade plan"}
            </button>
          </div>
        </div>
        <div className="card" style={{ padding: 20 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>— {tr ? "Otomatik topup" : "Auto-topup"}</div>
          <div style={{ fontSize: 13, color: "var(--tx2)" }}>
            {credits.auto_topup_enabled
              ? (tr ? `${credits.auto_topup_threshold} altına düşünce ${credits.auto_topup_pack_id} otomatik alınır.` : `Below ${credits.auto_topup_threshold} credits, ${credits.auto_topup_pack_id} pack auto-buys.`)
              : (tr ? "Kapalı. Krediler bitince üretim duraklar." : "Off. Generation pauses when out of credits.")}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="mono" style={{ fontSize: 10, color: "var(--mu)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <div className="t-num" style={{ fontSize: 20 }}>{value}</div>
    </div>
  );
}

function PoolCard({ label, sub, value, tone }: { label: string; sub: string; value: number; tone: string }) {
  return (
    <div style={{ background: "var(--base)", border: "1px solid var(--b2)", borderRadius: 8, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ width: 6, height: 6, borderRadius: 3, background: tone }} />
        <span className="mono" style={{ fontSize: 10, color: "var(--mu)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</span>
      </div>
      <div className="t-num" style={{ fontSize: 22, marginBottom: 4 }}>{value.toLocaleString("tr-TR")}</div>
      <div style={{ fontSize: 10, color: "var(--mu)" }}>{sub}</div>
    </div>
  );
}

function PlansGrid() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const [interval, setInterval] = useState<"monthly" | "annual">("monthly");
  const { data: plansRes } = useQuery({
    queryKey: ["billing", "plans", interval, i18n.resolvedLanguage],
    queryFn: () => api<{ plans: PlanTier[]; market?: MarketEnvelope } & { plans?: PlanTier[] }>(`/billing/plans?interval=${interval}&lang=${i18n.resolvedLanguage}`),
  });
  const { data: active } = useQuery({
    queryKey: ["billing", "plan", i18n.resolvedLanguage],
    queryFn: () => api<PlanResponse>(`/billing/plan?lang=${i18n.resolvedLanguage}`),
  });
  // Phase 7 (frontend) — checkout still POSTs the slug; backend Subscribe
  // handler resolves region+currency from the same geo headers the catalog
  // endpoint used, so prices are consistent end-to-end.
  const [promoCode, setPromoCode] = useState("");
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  // Buyer identity for the iyzico checkout form (v0.2.x real payments).
  const { user } = useAuth();
  const [pay, setPay] = useState<CheckoutInit | null>(null);
  const subscribe = useMutation({
    // `?interval=` is REQUIRED on the query string. billing-service resolves
    // the price through resolveMarket(r, …), which reads the interval from
    // the request QUERY (internal/handlers/geo.go) and defaults to "monthly".
    // Plan ids are identical across intervals, so omitting it silently
    // charged the monthly price for an annual subscription instead of
    // erroring. Not a body field — the body decoder never sees it.
    mutationFn: (planId: string) => api<CheckoutInit>(`/billing/subscribe?interval=${interval}`, {
      method: "POST",
      // api() JSON.stringifies body itself — pass a raw object, NOT a
      // pre-stringified string (that double-encodes → server gets a JSON
      // string → 400 "invalid json body"). v0.1.101.
      body: {
        plan_id: planId,
        promo_code: promoCode || undefined,
        buyer_email: user?.email,
        buyer_name: user?.display_name,
      },
    }),
    // Prefer the embedded modal (stay on our site); fall back to the hosted
    // page if the backend didn't return form content. v0.1.102.
    onSuccess: (data) => {
      if (data.checkout_form_content && data.token) setPay(data);
      else if (data.checkout_url) window.location.href = data.checkout_url;
      else alert(tr ? "Ödeme başlatılamadı, tekrar deneyin." : "Couldn't start checkout, please try again.");
    },
    onError: () => alert(tr ? "Ödeme başlatılamadı, tekrar deneyin." : "Couldn't start checkout, please try again."),
  });

  if (!plansRes) return <SkeletonRow />;
  const plans = plansRes.plans ?? [];
  const market = plansRes.market;
  const currency = market?.currency ?? "USD";
  const region = market?.region ?? "international";
  const activePlan = active?.plan ?? (active as PlanTier | undefined);

  return (
    <>
      {pay && pay.checkout_form_content && pay.token ? (
        <PaymentModal content={pay.checkout_form_content} token={pay.token} onClose={() => setPay(null)} />
      ) : null}
      {/* Interval toggle + currency badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", border: "1px solid var(--b2)", borderRadius: 6, overflow: "hidden" }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setInterval("monthly")}
            style={{
              borderRadius: 0,
              background: interval === "monthly" ? "var(--accent)" : "transparent",
              color: interval === "monthly" ? "#fff" : "var(--tx2)",
            }}
          >
            {tr ? "Aylık" : "Monthly"}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setInterval("annual")}
            style={{
              borderRadius: 0,
              background: interval === "annual" ? "var(--accent)" : "transparent",
              color: interval === "annual" ? "#fff" : "var(--tx2)",
            }}
          >
            {tr ? "Yıllık (%17 indirim)" : "Annual (17% off)"}
          </button>
        </div>
        <span className="mono" style={{ fontSize: 11, color: "var(--mu)" }}>
          {region === "turkey"
            ? (tr ? `Türkiye fiyatlandırması · ${currency} + %20 KDV` : `Turkey pricing · ${currency} + 20% KDV`)
            : `${currency} · ${tr ? "uluslararası fiyatlandırma" : "international pricing"}`}
        </span>
      </div>

      {/* Plan grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
        {plans.map((p) => {
          const isCurrent = activePlan?.slug === p.slug || activePlan?.id === p.id;
          const amount = p.amount ?? p.price_try ?? 0;
          const free = amount === 0 && !p.is_unlimited;
          const isPOA = p.is_unlimited && amount === 0;
          return (
            <div
              key={p.id || p.slug}
              className="card"
              style={{
                padding: 22,
                display: "flex",
                flexDirection: "column",
                gap: 12,
                borderColor: isCurrent ? "var(--accent)" : "var(--b2)",
              }}
            >
              <div>
                <div className="eyebrow" style={{ marginBottom: 8 }}>— {p.name}</div>
                <div style={{ fontSize: 13, color: "var(--tx2)", minHeight: 36 }}>{p.tagline}</div>
              </div>

              <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <span className="t-num" style={{ fontSize: 28 }}>
                  {isPOA ? "POA" : free ? (tr ? "Ücretsiz" : "Free") : formatMoney(amount, p.currency ?? currency)}
                </span>
                {!isPOA && !free ? (
                  <span style={{ fontSize: 12, color: "var(--mu)" }}>
                    / {interval === "annual" ? (tr ? "yıl" : "yr") : (tr ? "ay" : "mo")}
                  </span>
                ) : null}
              </div>

              {/* KDV note for Turkey region rows */}
              {(p.vat_rate ?? 0) > 0 ? (
                <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                  + %{Math.round((p.vat_rate ?? 0) * 100)} KDV
                  {" · "}
                  {tr ? "ödemede" : "at checkout"}
                  {" "}
                  {formatMoney((p.amount_with_vat ?? amount), p.currency ?? currency)}
                </div>
              ) : null}

              <div style={{ fontSize: 12, color: "var(--tx)" }}>
                {p.is_unlimited
                  ? "∞"
                  : `${(p.monthly_publications ?? p.monthly_credits ?? 0).toLocaleString()}`}{" "}
                {tr ? "gönderi/ay" : "posts/mo"}{" · "}
                {p.max_brands === 0
                  ? (tr ? "sınırsız marka" : "unlimited brands")
                  : `${p.max_brands} ${tr ? "marka" : "brand(s)"}`}
              </div>

              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                {p.features.map((f) => (
                  <li key={f} style={{ fontSize: 12, color: "var(--tx2)", display: "flex", alignItems: "center", gap: 6 }}>
                    <Icons.Check size={12} style={{ color: "var(--success)", flexShrink: 0 }} /> {f}
                  </li>
                ))}
              </ul>

              <button
                className={isCurrent ? "btn btn-secondary" : "btn btn-primary"}
                disabled={isCurrent || subscribe.isPending}
                onClick={() => {
                  setSelectedSlug(p.slug ?? p.id);
                  subscribe.mutate(p.slug ?? p.id);
                }}
                style={{ width: "100%", justifyContent: "center", marginTop: 8 }}
              >
                {isCurrent
                  ? (tr ? "Mevcut plan" : "Current plan")
                  : isPOA
                    ? (tr ? "Bize ulaş" : "Talk to us")
                    : (tr ? "Seç" : "Choose")}
              </button>
            </div>
          );
        })}
      </div>

      {/* Promo code row — applies to whichever plan the user picks next.
          Validation calls /v1/billing/promo/validate so the user sees the
          discount BEFORE landing on the checkout page. */}
      <PromoCodeRow
        kind="plan"
        slug={selectedSlug ?? plans[1]?.slug ?? null}
        interval={interval}
        currency={currency}
        promoCode={promoCode}
        setPromoCode={setPromoCode}
        tr={tr}
      />
    </>
  );
}

function CreditPacksTab() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  // Buyer identity for the iyzico checkout form — see the buy mutation.
  const { user } = useAuth();
  const { data: packsRes } = useQuery({
    queryKey: ["billing", "packs", i18n.resolvedLanguage],
    queryFn: () => api<{ packs: CreditPack[]; market?: MarketEnvelope } & { packs?: CreditPack[] }>(`/billing/packs?lang=${i18n.resolvedLanguage}`),
  });
  const [promoCode, setPromoCode] = useState("");
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [pay, setPay] = useState<CheckoutInit | null>(null);
  const buy = useMutation({
    mutationFn: (packId: string) => api<CheckoutInit>("/billing/credits/buy", {
      method: "POST",
      // buyer_email/buyer_name are read by buyPackViaIyzico and fall back to
      // billing@nexusdijital.com / "Nexus Customer" when absent — so every
      // credit-pack payment used to reach iyzico with a placeholder buyer.
      // The subscribe call above already sends them.
      body: {
        pack_id: packId,
        promo_code: promoCode || undefined,
        buyer_email: user?.email,
        buyer_name: user?.display_name,
      },
    }),
    // Prefer the embedded modal; fall back to the hosted page. v0.1.102.
    onSuccess: (data) => {
      if (data.checkout_form_content && data.token) setPay(data);
      else if (data.checkout_url) window.location.href = data.checkout_url;
      else alert(tr ? "Ödeme başlatılamadı, tekrar deneyin." : "Couldn't start checkout, please try again.");
    },
    onError: () => {
      alert(tr ? "Ödeme başlatılamadı, tekrar deneyin." : "Couldn't start checkout, please try again.");
    },
  });
  if (!packsRes) return <SkeletonRow />;
  const packs = packsRes.packs ?? [];
  const market = packsRes.market;
  const currency = market?.currency ?? "USD";
  const region = market?.region ?? "international";

  return (
    <>
      {pay && pay.checkout_form_content && pay.token ? (
        <PaymentModal content={pay.checkout_form_content} token={pay.token} onClose={() => setPay(null)} />
      ) : null}
      <div style={{ marginBottom: 16, color: "var(--tx2)", fontSize: 13, maxWidth: 600 }}>
        {tr
          ? "Aylık gönderi limitin biterse veya proaktif doldurmak için ek kredi paketleri satın al. Aylık sıfırlamadan etkilenmezler."
          : "Top-up packs let you keep generating after your monthly limit runs out. They persist across monthly reset."}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center" }}>
        <span className="mono" style={{ fontSize: 11, color: "var(--mu)" }}>
          {region === "turkey"
            ? (tr ? `Türkiye fiyatlandırması · ${currency} + %20 KDV` : `Turkey pricing · ${currency} + 20% KDV`)
            : `${currency} · ${tr ? "uluslararası fiyatlandırma" : "international pricing"}`}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {packs.map((p) => {
          const amount = p.amount ?? p.price_try ?? 0;
          const finalAmount = p.amount_with_vat ?? amount;
          return (
            <div key={p.id || p.slug} className="card" style={{ padding: 22, position: "relative", display: "flex", flexDirection: "column", gap: 10 }}>
              {p.badge ? (
                <span className="badge badge-red" style={{ position: "absolute", top: 12, right: 12, fontSize: 9, padding: "2px 8px" }}>
                  {p.badge}
                </span>
              ) : null}
              <div className="eyebrow">— {p.name}</div>
              <div className="t-num" style={{ fontSize: 28 }}>{p.credits.toLocaleString()}</div>
              <div style={{ fontSize: 13, color: "var(--tx2)" }}>{tr ? "kredi" : "credits"}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontSize: 18, fontWeight: 600 }}>{formatMoney(amount, p.currency ?? currency)}</span>
                {p.price_per_unit != null ? (
                  <span style={{ fontSize: 11, color: "var(--mu)" }}>
                    · {formatMoney(p.price_per_unit, p.currency ?? currency)}/cr
                  </span>
                ) : p.price_per_credit ? (
                  <span style={{ fontSize: 11, color: "var(--mu)" }}>· {p.price_per_credit}</span>
                ) : null}
              </div>
              {(p.vat_rate ?? 0) > 0 ? (
                <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                  + %{Math.round((p.vat_rate ?? 0) * 100)} KDV · {tr ? "ödemede" : "at checkout"} {formatMoney(finalAmount, p.currency ?? currency)}
                </div>
              ) : null}
              <button
                className="btn btn-primary"
                disabled={buy.isPending}
                onClick={() => {
                  setSelectedSlug(p.slug ?? p.id);
                  buy.mutate(p.slug ?? p.id);
                }}
                style={{ width: "100%", justifyContent: "center", marginTop: 8 }}
              >
                {tr ? "Satın al" : "Buy"}
              </button>
            </div>
          );
        })}
      </div>

      <PromoCodeRow
        kind="pack"
        slug={selectedSlug ?? packs[1]?.slug ?? null}
        interval="monthly"
        currency={currency}
        promoCode={promoCode}
        setPromoCode={setPromoCode}
        tr={tr}
      />
    </>
  );
}

// ── Promo code row — preview the discount before checkout ─────────────────

type PromoValidationResp = {
  valid: boolean;
  error_code?: string;
  message?: string;
  code?: string;
  description?: string;
  discount_type?: string;
  discount_value?: number;
  base_amount?: number;
  amount_off?: number;
  final_amount?: number;
  currency?: string;
};

function PromoCodeRow({
  kind,
  slug,
  interval,
  currency,
  promoCode,
  setPromoCode,
  tr,
}: {
  kind: "plan" | "pack";
  slug: string | null;
  interval: string;
  currency: string;
  promoCode: string;
  setPromoCode: (s: string) => void;
  tr: boolean;
}) {
  const [draft, setDraft] = useState(promoCode);
  const [result, setResult] = useState<PromoValidationResp | null>(null);
  const validate = useMutation({
    mutationFn: (code: string) =>
      api<PromoValidationResp>("/billing/promo/validate", {
        method: "POST",
        body: {
          code,
          [kind === "plan" ? "plan_slug" : "pack_slug"]: slug,
          interval,
        },
      }),
    onSuccess: (r) => {
      setResult(r);
      if (r.valid && r.code) setPromoCode(r.code);
    },
  });

  return (
    <div
      className="card"
      style={{
        padding: 18,
        marginTop: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        background: "var(--surface)",
      }}
    >
      <div className="eyebrow">— {tr ? "Promosyon kodu" : "Promo code"}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          className="input"
          placeholder={tr ? "Kod (ör. SUMMER25)" : "Code (e.g. SUMMER25)"}
          value={draft}
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draft.trim() && slug) validate.mutate(draft.trim());
          }}
          style={{ flex: 1, minWidth: 160 }}
        />
        <button
          className="btn btn-secondary btn-sm"
          disabled={!draft.trim() || !slug || validate.isPending}
          onClick={() => validate.mutate(draft.trim())}
        >
          {validate.isPending ? (tr ? "Kontrol ediliyor…" : "Checking…") : tr ? "Uygula" : "Apply"}
        </button>
      </div>
      {result ? (
        result.valid ? (
          <div style={{ fontSize: 12, color: "var(--success)" }}>
            ✓ {result.description || result.code} —{" "}
            {tr ? "indirim" : "off"}{" "}
            {formatMoney(result.amount_off ?? 0, result.currency ?? currency)}{" "}
            {tr ? "(toplam" : "(total"}{" "}
            {formatMoney(result.final_amount ?? 0, result.currency ?? currency)})
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "var(--error)" }}>
            ⚠ {result.message ?? (tr ? "Geçersiz kod" : "Invalid code")}
          </div>
        )
      ) : null}
    </div>
  );
}

function UsageTab() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const { data } = useQuery({ queryKey: ["billing", "usage"], queryFn: () => api<UsageResponse>("/billing/usage?period=30d") });
  if (!data) return <SkeletonRow />;
  const max = Math.max(...data.by_kind.map((b) => b.credits), 1);
  return (
    <div className="bill-2col" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>— {tr ? "Türe göre" : "By kind"} · {data.period}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {data.by_kind.map((k) => (
            <div key={k.kind}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                <span>{k.kind}</span>
                <span className="t-num">{k.credits.toLocaleString("tr-TR")}</span>
              </div>
              <div style={{ height: 6, background: "var(--elev)", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${(k.credits / max) * 100}%`, height: "100%", background: "var(--accent)" }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>— {tr ? "Kaynağa göre" : "By source"}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {data.by_source.map((s) => (
            <div key={s.source} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <span style={{ color: "var(--tx2)" }}>{s.source}</span>
              <span className="t-num">{s.credits.toLocaleString("tr-TR")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InvoicesTable() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const { data } = useQuery({ queryKey: ["billing", "invoices"], queryFn: () => api<{ data: Invoice[] }>("/billing/invoices?limit=50") });
  if (!data) return <SkeletonRow />;
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {([tr ? "Numara" : "Number", tr ? "Tür" : "Kind", tr ? "Dönem" : "Period", tr ? "Tutar" : "Amount", tr ? "Durum" : "Status", ""] as const).map((h, i) => (
              <th key={i} className="mono" style={{ fontSize: 10, color: "var(--mu)", letterSpacing: "0.15em", textTransform: "uppercase", textAlign: "left", padding: "12px 16px", background: "var(--surface)", borderBottom: "1px solid var(--b1)", fontWeight: 500 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.data.map((inv) => (
            <tr key={inv.id} style={{ borderBottom: "1px solid var(--b1)" }}>
              <td style={{ padding: "14px 16px", color: "var(--tx)", fontFamily: "var(--mono)" }}>{inv.number}</td>
              <td style={{ padding: "14px 16px", color: "var(--tx2)" }}>{inv.kind}</td>
              <td style={{ padding: "14px 16px", color: "var(--tx2)" }}>{new Date(inv.period_start).toLocaleDateString(i18n.resolvedLanguage)}</td>
              <td style={{ padding: "14px 16px", color: "var(--tx)" }}>₺{inv.amount_try.toLocaleString("tr-TR")}</td>
              <td style={{ padding: "14px 16px" }}>
                <span className={`badge ${inv.status === "paid" ? "badge-green" : "badge-amber"}`}>
                  <span className="dot" />
                  {inv.status}
                </span>
              </td>
              <td style={{ padding: "14px 16px", textAlign: "right" }}>
                {inv.pdf_url && inv.pdf_url !== "#" ? <a href={inv.pdf_url} className="btn btn-ghost btn-sm"><Icons.Download size={12} /> PDF</a> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="skel" style={{ height: 100, borderRadius: 12 }} />
      ))}
    </div>
  );
}
